import {
  ClickAwayListener,
  IconButton,
  InputBase,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  Avatar,
  useTheme,
} from "@material-ui/core";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import BlockIcon from "@material-ui/icons/Block";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import AddIcon from "@material-ui/icons/Add";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import React, { useContext, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";

import MicIcon from "@mui/icons-material/Mic";
import CircularProgress from "@mui/material/CircularProgress";
import MicRecorder from "mic-recorder-to-mp3";
import MarkdownWrapper from "../../components/MarkdownWrapper";
import RecordingTimer from "../../components/MessageInput/RecordingTimer";
import ModalImageCors from "../../components/ModalImageCors";
import toastError from "../../errors/toastError";
import AudioModal from "../../components/AudioModal";
import DocumentModal from "../../components/DocumentModal";
import { fileInfoFromUrl } from "../../components/MessagesList/FileCard";

import { format, isSameDay, parseISO } from "date-fns";
import ReplyIcon from "@mui/icons-material/Reply";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ForwardIcon from "@mui/icons-material/Forward";
import { getTokens } from "../../styles/tokens";
import { getWa, senderColor } from "./chatTheme";

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  const isDark = theme.palette.type === "dark";
  const wa = getWa(isDark);
  const bubbleBase = {
    position: "relative",
    minWidth: 82,
    maxWidth: "min(640px, 65%)",
    padding: "6px 7px 5px 9px",
    borderRadius: 7.5,
    boxShadow: wa.shadow,
    color: wa.text,
    fontSize: 14.2,
    lineHeight: "19px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: { maxWidth: "86%" },
    "&:hover $bubbleMenuBtn, &:focus-within $bubbleMenuBtn": { opacity: 1 },
  };
  return {
    paper: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      overflow: "hidden",
      backgroundColor: wa.bg,
      border: 0,
      borderRadius: 0,
      boxShadow: "none",
    },
    // cabeçalho da conversa no padrão do WhatsApp Web: faixa cinza plana
    conversationHeader: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 12,
      height: 59,
      padding: "0 16px",
      backgroundColor: wa.strip,
      boxSizing: "border-box",
      [theme.breakpoints.down("sm")]: { padding: "0 10px 0 4px", gap: 8 },
    },
    backButton: {
      width: 36,
      height: 36,
      padding: 0,
      borderRadius: "50%",
      color: wa.icon,
    },
    headerAvatar: {
      width: 40,
      height: 40,
      flex: "none",
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    headerTitle: {
      overflow: "hidden",
      color: tk.text,
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "21px",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    headerSubtitle: {
      overflow: "hidden",
      color: tk.textSecondary,
      fontSize: 13,
      lineHeight: "18px",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    messageList: {
      position: "relative",
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: "8px 0 10px",
      backgroundColor: wa.bg,
      backgroundImage: wa.wallpaper,
      ...theme.scrollbarStyles,
    },
    loadingMore: {
      position: "sticky",
      top: 4,
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "max-content",
      maxWidth: "calc(100% - 32px)",
      margin: "0 auto 6px",
      padding: "5px 12px",
      borderRadius: 7.5,
      backgroundColor: wa.pill,
      boxShadow: wa.shadow,
      color: wa.pillText,
      fontSize: 12.5,
    },
    dailyTimestamp: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      margin: "10px 0 8px",
    },
    dailyTimestampText: {
      padding: "5px 12px 6px",
      borderRadius: 7.5,
      backgroundColor: wa.pill,
      boxShadow: wa.shadow,
      color: wa.pillText,
      fontSize: 12.5,
      lineHeight: "15px",
    },
    messageRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: 6,
      margin: "2px 0 0",
      padding: "0 5%",
      transition: "background-color 0.5s",
      [theme.breakpoints.down("sm")]: { padding: "0 10px" },
    },
    messageRowSelf: {
      justifyContent: "flex-end",
    },
    // primeira mensagem de uma sequência da mesma pessoa
    messageRowFirst: {
      marginTop: 10,
    },
    avatarSlot: {
      flex: "none",
      width: 28,
      height: 28,
      "& .MuiAvatar-root": {
        width: 28,
        height: 28,
        fontSize: 12,
      },
    },
    bubbleIn: {
      ...bubbleBase,
      backgroundColor: wa.incoming,
    },
    bubbleOut: {
      ...bubbleBase,
      backgroundColor: wa.outgoing,
    },
    bubbleMenuBtn: {
      position: "absolute",
      top: 2,
      right: 2,
      zIndex: 1,
      width: 24,
      height: 24,
      padding: 0,
      borderRadius: "50%",
      color: wa.meta,
      backgroundColor: "inherit",
      opacity: 0,
      transition: "opacity 120ms ease",
      "&:hover": { backgroundColor: "inherit", color: wa.text },
      "&:focus-visible": { opacity: 1 },
      "& svg": { fontSize: 20 },
      // sem mouse (toque) não há "passar por cima": o botão fica sempre à vista
      "@media (hover: none)": { opacity: 1 },
    },
    senderName: {
      marginBottom: 2,
      paddingRight: 22,
      fontSize: 12.8,
      fontWeight: 500,
      lineHeight: "17px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    bubbleContent: {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      "& p": { margin: 0 },
      "& a": { color: isDark ? "#53bdeb" : "#027eb5" },
    },
    bubbleMeta: {
      display: "block",
      marginTop: 2,
      textAlign: "right",
      color: wa.meta,
      fontSize: 11,
      lineHeight: "15px",
      fontVariantNumeric: "tabular-nums",
      userSelect: "none",
    },
    deletedText: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      color: wa.meta,
      fontStyle: "italic",
      "& svg": { fontSize: 16 },
    },
    deletedAdminNote: {
      display: "block",
      marginBottom: 4,
      color: wa.meta,
      fontSize: 12,
      fontStyle: "italic",
    },
    messageMedia: {
      display: "block",
      width: 280,
      maxWidth: "100%",
      maxHeight: 320,
      margin: "0 0 3px -3px",
      borderRadius: 6,
      backgroundColor: "#000",
    },
    // cartão de arquivo, como o da conversa de atendimento
    fileCard: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      minWidth: 220,
      maxWidth: 340,
      margin: "0 0 4px -3px",
      padding: "9px 8px 9px 10px",
      border: 0,
      borderRadius: 6,
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(11,20,26,0.05)",
      color: "inherit",
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(11,20,26,0.08)",
      },
      "&:focus-visible": {
        outline: `2px solid ${wa.accent}`,
        outlineOffset: 1,
      },
      [theme.breakpoints.down("xs")]: { minWidth: 0 },
    },
    fileBadge: {
      flex: "none",
      width: 34,
      height: 40,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 5,
      backgroundColor: wa.meta,
      color: "#ffffff",
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
    },
    fileInfo: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    fileName: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: 14,
      lineHeight: "19px",
    },
    fileHint: {
      color: wa.meta,
      fontSize: 12,
      lineHeight: "16px",
    },
    fileAction: {
      flex: "none",
      display: "flex",
      color: wa.meta,
      "& svg": { fontSize: 20 },
    },
    // ---------- barra de digitação: faixa cinza + campo claro ----------
    inputArea: {
      position: "relative",
      flex: "none",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      padding: "8px 12px",
      backgroundColor: wa.strip,
      [theme.breakpoints.down("sm")]: {
        padding: "8px 9px calc(8px + env(safe-area-inset-bottom))",
      },
    },
    inputBox: {
      display: "flex",
      alignItems: "flex-end",
      gap: 2,
      width: "100%",
      padding: 5,
      borderRadius: 10,
      backgroundColor: wa.field,
      boxSizing: "border-box",
    },
    barButton: {
      flex: "none",
      width: 36,
      height: 36,
      padding: 7,
      borderRadius: "50%",
      color: wa.icon,
      "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,20,26,0.06)" },
    },
    input: {
      flex: 1,
      minWidth: 0,
      padding: "6px 6px",
      color: tk.text,
      fontSize: 15,
      lineHeight: "22px",
      "& textarea::placeholder": { color: tk.textMuted, opacity: 1 },
    },
    sendButton: {
      flex: "none",
      width: 36,
      height: 36,
      padding: 0,
      borderRadius: "50%",
      color: tk.accentOn,
      backgroundColor: tk.accent,
      "&:hover": { backgroundColor: tk.accentHover },
      "&.Mui-disabled": { backgroundColor: tk.muted, color: tk.textMuted },
      "& svg": { fontSize: 20 },
    },
    uploadInput: {
      display: "none",
    },
    actionBar: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 46,
      padding: "5px 5px 5px 8px",
      borderRadius: 10,
      backgroundColor: wa.field,
      boxSizing: "border-box",
    },
    actionBarText: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: tk.text,
      fontSize: 14,
    },
    recordDot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
      backgroundColor: tk.semantic.error,
      animation: "$recPulse 1.2s ease-in-out infinite",
    },
    "@keyframes recPulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.25 },
    },
    recordTime: {
      marginRight: "auto",
      color: tk.text,
      fontSize: 15,
      fontVariantNumeric: "tabular-nums",
    },
    cancelAudioIcon: {
      color: tk.semantic.error,
    },
    audioLoading: {
      color: wa.accent,
    },
    emojiBox: {
      position: "absolute",
      bottom: "calc(100% + 4px)",
      left: 10,
      zIndex: 1000,
      maxWidth: "calc(100vw - 20px)",
    },
    replyBar: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 6px 6px 12px",
      borderRadius: 8,
      borderLeft: `4px solid ${wa.accent}`,
      backgroundColor: wa.field,
    },
    replyContent: {
      flex: 1,
      minWidth: 0,
    },
    replySender: {
      color: wa.accent,
      fontSize: 12.8,
      fontWeight: 500,
      lineHeight: "17px",
    },
    replyText: {
      color: tk.textSecondary,
      fontSize: 13,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    replyPreview: {
      marginBottom: 4,
      padding: "4px 8px 5px",
      borderLeft: `4px solid ${wa.accent}`,
      borderRadius: 5,
      backgroundColor: wa.quote,
      cursor: "pointer",
    },
    replyPreviewSender: {
      color: wa.accent,
      fontSize: 12.8,
      fontWeight: 500,
      lineHeight: "17px",
    },
    replyPreviewText: {
      color: wa.meta,
      fontSize: 13,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    highlightedMessage: {
      backgroundColor: isDark ? "rgba(255,213,79,0.22)" : "rgba(255,213,79,0.5)",
    },
    forwardedMessageHeader: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginBottom: 3,
      color: wa.meta,
      fontSize: 12,
      fontStyle: "italic",
      "& svg": { fontSize: 15 },
    },
    menuIcon: {
      minWidth: 32,
      color: tk.textSecondary,
      "& svg": { fontSize: 19 },
    },
    menuDanger: {
      color: tk.semantic.error,
      "& $menuIcon": { color: tk.semantic.error },
    },
  };
});

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loadingMore,
  onEdit,
  onDelete,
  onForward,
  // messageListRef,
  // isFirstPage,
  justOpenedChat,
  setJustOpenedChat,
  addOptimisticMessage,
  onBack,
}) {
  const topRef = useRef(null);

  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const { user } = useContext(AuthContext);
  const baseRef = useRef();
  const messageRefs = useRef({});

  const [contentMessage, setContentMessage] = useState("");
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const inputRef = useRef();
  const [replyingTo, setReplyingTo] = useState(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(false);
  // menu de opções da mensagem (seta no canto da bolha)
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuMessage, setMenuMessage] = useState(null);

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const unreadMessages = (chat) => {
    if (chat && chat.users && Array.isArray(chat.users)) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser && currentUser.unreads > 0;
    }
    return false;
  };

  useEffect(() => {
    if (justOpenedChat) {
      setInitialScrollDone(false);
    }
  }, [justOpenedChat]);

  useEffect(() => {
    if (chat && chat.id && unreadMessages(chat)) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {
        console.error("Erro ao marcar mensagens como lidas:", err);
      }
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!messages || messages.length === 0 || loadingMore || initialScrollDone)
      return;

    const scrollToBottomSafely = () => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const container = document.querySelector(".messageList");
          if (container) {
            container.scrollTop = container.scrollHeight;
            setInitialScrollDone(true);
          }
        }, 100); // Aumentar o timeout para dar tempo das mensagens renderizarem
      });
    };

    scrollToBottomSafely();
  }, [messages, loadingMore, initialScrollDone]);

  // Após o scroll automático inicial, liberar o carregamento incremental
  useEffect(() => {
    if (!loading && !loadingMore && initialScrollDone) {
      setCanLoadMore(true);
    }
  }, [loading, loadingMore, initialScrollDone]);

  // Adicionar um useEffect separado para quando justOpenedChat muda
  useEffect(() => {
    if (justOpenedChat && messages && messages.length > 0) {
      const scrollToBottomSafely = () => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const container = document.querySelector(".messageList");
            if (container) {
              container.scrollTop = container.scrollHeight;
              setJustOpenedChat(false); // Resetar após executar o scroll
            }
          }, 200); // Timeout maior para garantir que tudo foi renderizado
        });
      };

      scrollToBottomSafely();
    }
  }, [justOpenedChat, messages]);

  const handleScroll = (e) => {
    if (!initialScrollDone || !canLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (!pageInfo?.hasMore || loading) return;

    if (scrollTop < 200) {
      console.log("Carregando mais mensagens... ScrollTop:", scrollTop);
      handleLoadMore();
    }
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handlePaste = (e) => {
    if (e.clipboardData.files.length > 0) {
      const selectedMedias = Array.from(e.clipboardData.files);
      setMedias(selectedMedias);
      e.preventDefault();
    }
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setContentMessage((prevState) => prevState + emoji);
    inputRef.current.focus();
  };

  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const checkMessageMedia = (message) => {
    if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaPath} />;
    }
    if (message.mediaType === "audio") {
      return (
        <AudioModal
          url={message.mediaPath}
          message={message}
          disableTranscription={!chat.isGroup}
        />
      );
    }
    if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaPath}
          controls
        />
      );
    } else {
      const info = fileInfoFromUrl(message.mediaPath, message.mediaName);
      return (
        <button
          type="button"
          className={classes.fileCard}
          onClick={() => {
            setSelectedDocument(message);
            setDocumentModalOpen(true);
          }}
        >
          <span className={classes.fileBadge}>{info.ext || "arq"}</span>
          <span className={classes.fileInfo}>
            <span className={classes.fileName}>
              {message.mediaName || info.name}
            </span>
            <span className={classes.fileHint}>Visualizar documento</span>
          </span>
          <span className={classes.fileAction}>
            <VisibilityOutlinedIcon />
          </span>
        </button>
      );
    }
  };

  const handleSendMedia = async (e) => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => {
      formData.append("typeArch", "chat");
      formData.append("fileId", chat.id);
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/chats/${chat.id}/messages`, formData);
    } catch (err) {
      console.log(err);
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();

      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `audio-${new Date().getTime()}.mp3`;
      formData.append("typeArch", "chat");
      formData.append("fileId", chat.id);
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/chats/${chat.id}/messages`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleSendMessageWithReply = async (content) => {
    if (content.trim() !== "") {
      // Adicione a mensagem localmente de forma otimista
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        chatId: chat.id,
        senderId: user.id,
        message: content,
        createdAt: new Date().toISOString(),
        sender: {
          id: user.id,
          name: user.name,
          profileImage: user.profileImage,
        },
        replyTo: replyingTo,
        isDeleted: false,
        isEdited: false,
        mediaType: "text",
      };
      addOptimisticMessage(optimisticMessage);
      setContentMessage("");
      setReplyingTo(null);
      await api.post(`/chats/${chat.id}/messages`, {
        message: content,
        replyToId: replyingTo?.id,
      });
      // O socket vai substituir a mensagem otimista pela real depois
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && contentMessage.trim() !== "") {
      handleSendMessageWithReply(contentMessage);
    }
  };

  const handleSendClick = () => {
    if (contentMessage.trim() !== "") {
      handleSendMessageWithReply(contentMessage);
    }
  };

  const handleEdit = (message) => {
    onEdit(message);
  };

  const handleDelete = (message) => {
    onDelete(message);
  };

  const handleForward = (message) => {
    onForward(message);
  };

  const isNewDay = (index, messagesList) =>
    index === 0 ||
    !isSameDay(
      parseISO(messagesList[index].createdAt),
      parseISO(messagesList[index - 1].createdAt)
    );

  const renderDailyTimestamps = (message, index, messagesList) => {
    if (!isNewDay(index, messagesList)) return null;
    const day = parseISO(messagesList[index].createdAt);
    return (
      <div className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
        <span className={classes.dailyTimestampText}>
          {isSameDay(day, new Date()) ? "Hoje" : format(day, "dd/MM/yyyy")}
        </span>
      </div>
    );
  };

  const openMessageMenu = (event, message) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuMessage(message);
  };

  const closeMessageMenu = () => {
    setMenuAnchor(null);
  };

  const runMenuAction = (action) => {
    const message = menuMessage;
    closeMessageMenu();
    if (message) action(message);
  };

  // Regra: só pode editar a própria mensagem, em até 10 minutos
  const canEditMessage = (message) =>
    message.senderId === user.id &&
    !message.isDeleted &&
    new Date() - new Date(message.createdAt) < 10 * 60 * 1000;

  const renderMessageBody = (item) => {
    const repliedMessage = item.replyTo;
    if (item.isDeleted && user.profile !== "admin") {
      return (
        <span className={classes.deletedText}>
          <BlockIcon />
          {i18n.t("chatMessages.deleted")}
        </span>
      );
    }
    return (
      <>
        {item.isDeleted && (
          <span className={classes.deletedAdminNote}>
            {i18n.t("chatMessages.deletedAdmin")}
          </span>
        )}
        {item.mediaPath && checkMessageMedia(item)}
        <MessageWithLineBreaks
          text={item.message}
          replyTo={repliedMessage}
          scrollToMessage={scrollToMessage}
          forwardedFrom={item.forwardedFrom}
          checkMessageMedia={checkMessageMedia}
        />
      </>
    );
  };

  const scrollToMessage = (messageId) => {
    const el = messageRefs.current[messageId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add(classes.highlightedMessage);
      setTimeout(() => el.classList.remove(classes.highlightedMessage), 1500);
    }
  };

  const otherParticipant =
    !chat?.isGroup &&
    chat?.users?.find((participant) => participant.userId !== user.id);
  const conversationName = chat?.isGroup
    ? chat.title || "Grupo"
    : otherParticipant?.user?.name || "Conversa";
  const rawConversationImage = chat?.isGroup
    ? chat.groupImage
    : otherParticipant?.user?.profileImage;
  const conversationImage = rawConversationImage
    ? rawConversationImage.startsWith("http")
      ? rawConversationImage
      : chat?.isGroup
      ? getBackendUrl() + rawConversationImage
      : getBackendUrl() +
        "/public/company" +
        otherParticipant?.user?.companyId +
        "/user/" +
        rawConversationImage
    : undefined;

  return (
    <>
      <Paper className={classes.paper} elevation={0} square>
        <header className={classes.conversationHeader}>
          {onBack && (
            <IconButton
              className={classes.backButton}
              onClick={onBack}
              aria-label="Voltar para a lista"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Avatar src={conversationImage} className={classes.headerAvatar}>
            {conversationName.charAt(0).toUpperCase()}
          </Avatar>
          <div className={classes.headerText}>
            <span className={classes.headerTitle}>{conversationName}</span>
            <span className={classes.headerSubtitle}>
              {chat?.isGroup
                ? (chat?.users?.length || 0) + " participantes"
                : "Conversa interna"}
            </span>
          </div>
        </header>
        {/* Área de mensagens rolável com o papel de parede da conversa */}
        <div
          className={`messageList ${classes.messageList}`}
          onScroll={handleScroll}
        >
          <div ref={topRef} style={{ height: 1 }} />
          {loadingMore && (
            <div className={classes.loadingMore}>
              <CircularProgress size={14} color="inherit" />
              <span>Carregando mensagens antigas...</span>
            </div>
          )}

          {Array.isArray(messages) &&
            messages.map((item, key) => {
              if (!item) return null;
              const mine = item.senderId === user.id;
              const previous = key > 0 ? messages[key - 1] : null;
              const firstOfRun =
                !previous ||
                previous.senderId !== item.senderId ||
                isNewDay(key, messages);
              const senderName =
                item.sender && item.sender.name ? item.sender.name : "Usuário";
              const showSender = chat?.isGroup && !mine;
              const canAct = !item.isDeleted || user.profile === "admin";

              return (
                <React.Fragment key={key}>
                  {renderDailyTimestamps(item, key, messages)}
                  <div
                    ref={(el) => (messageRefs.current[item.id] = el)}
                    className={clsx(classes.messageRow, {
                      [classes.messageRowSelf]: mine,
                      [classes.messageRowFirst]: firstOfRun,
                    })}
                    onDoubleClick={() => handleReply(item)}
                    title="Clique duas vezes para responder"
                  >
                    {showSender && (
                      <div className={classes.avatarSlot}>
                        {firstOfRun && (
                          <Avatar
                            src={
                              item.sender && item.sender.profileImage
                                ? `${getBackendUrl()}/public/company${item.companyId}/user/${item.sender.profileImage}`
                                : undefined
                            }
                            alt={senderName}
                          >
                            {(!item.sender || !item.sender.profileImage) &&
                              (item.sender && item.sender.name
                                ? item.sender.name.charAt(0)
                                : "?")}
                          </Avatar>
                        )}
                      </div>
                    )}
                    <div
                      className={mine ? classes.bubbleOut : classes.bubbleIn}
                    >
                      {canAct && (
                        <IconButton
                          className={classes.bubbleMenuBtn}
                          onClick={(e) => openMessageMenu(e, item)}
                          onDoubleClick={(e) => e.stopPropagation()}
                          aria-label="Opções da mensagem"
                          aria-haspopup="true"
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      )}
                      {showSender && firstOfRun && (
                        <div
                          className={classes.senderName}
                          style={{ color: senderColor(item.senderId, isDark) }}
                        >
                          {senderName}
                        </div>
                      )}
                      <div className={classes.bubbleContent}>
                        {renderMessageBody(item)}
                      </div>
                      <span className={classes.bubbleMeta}>
                        {format(new Date(item.createdAt), "HH:mm")}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

          <div ref={baseRef}></div>
        </div>
        {/* Barra de digitação fixa */}
        <div className={classes.inputArea}>
          {replyingTo && (
            <div className={classes.replyBar}>
              <div className={classes.replyContent}>
                <div className={classes.replySender}>
                  {i18n.t("chatMessages.replyingTo")} {replyingTo.sender.name}
                </div>
                <div className={classes.replyText}>{replyingTo.message}</div>
              </div>
              <IconButton
                className={classes.barButton}
                onClick={handleCancelReply}
                aria-label="Cancelar resposta"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          )}
          {recording ? (
            <div className={classes.actionBar}>
              <IconButton
                className={classes.barButton}
                aria-label="Cancelar gravação"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <DeleteOutlineIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <CircularProgress size={22} className={classes.audioLoading} />
              ) : (
                <>
                  <span className={classes.recordDot} />
                  <div className={classes.recordTime}>
                    <RecordingTimer />
                  </div>
                </>
              )}
              <IconButton
                className={classes.sendButton}
                aria-label="Enviar áudio"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <SendIcon />
              </IconButton>
            </div>
          ) : medias.length > 0 ? (
            <div className={classes.actionBar}>
              <IconButton
                className={classes.barButton}
                aria-label="Cancelar envio do arquivo"
                onClick={(e) => setMedias([])}
              >
                <CloseIcon />
              </IconButton>
              {loading ? (
                <span className={classes.actionBarText}>
                  <CircularProgress size={20} className={classes.audioLoading} />
                </span>
              ) : (
                <span className={classes.actionBarText}>
                  {medias[0]?.name}
                  {medias.length > 1 ? ` +${medias.length - 1}` : ""}
                </span>
              )}
              <IconButton
                className={classes.sendButton}
                aria-label="Enviar arquivo"
                onClick={handleSendMedia}
                disabled={loading}
              >
                <SendIcon />
              </IconButton>
            </div>
          ) : (
            <div className={classes.inputBox}>
              <FileInput
                disableOption={loading}
                handleChangeMedias={handleChangeMedias}
              />
              <IconButton
                className={classes.barButton}
                aria-label="Emojis"
                disabled={loading}
                onClick={(e) => setShowEmoji((prevState) => !prevState)}
              >
                <MoodIcon />
              </IconButton>
              {showEmoji ? (
                <div className={classes.emojiBox}>
                  <ClickAwayListener onClickAway={(e) => setShowEmoji(false)}>
                    <Picker
                      perLine={window.innerWidth < 640 ? 8 : 16}
                      theme={isDark ? "dark" : "light"}
                      showPreview={true}
                      showSkinTones={false}
                      onSelect={handleAddEmoji}
                    />
                  </ClickAwayListener>
                </div>
              ) : null}
              <InputBase
                inputRef={inputRef}
                multiline
                maxRows={5}
                value={contentMessage}
                onKeyUp={handleKeyPress}
                onChange={(e) => setContentMessage(e.target.value)}
                onPaste={handlePaste}
                className={classes.input}
                placeholder={i18n.t("chatMessages.typeMessage")}
                inputProps={{ "aria-label": i18n.t("chatMessages.typeMessage") }}
              />
              {contentMessage ? (
                <IconButton
                  onClick={handleSendClick}
                  className={classes.sendButton}
                  aria-label={i18n.t("chatMessages.send")}
                >
                  <SendIcon />
                </IconButton>
              ) : (
                <IconButton
                  className={classes.barButton}
                  aria-label="Gravar áudio"
                  disabled={loading}
                  onClick={handleStartRecording}
                >
                  <MicIcon />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMessageMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => runMenuAction(handleReply)}>
          <ListItemIcon className={classes.menuIcon}>
            <ReplyIcon />
          </ListItemIcon>
          {i18n.t("chatMessages.reply")}
        </MenuItem>
        <MenuItem onClick={() => runMenuAction(handleForward)}>
          <ListItemIcon className={classes.menuIcon}>
            <ForwardIcon />
          </ListItemIcon>
          {i18n.t("chatMessages.forward")}
        </MenuItem>
        {menuMessage && canEditMessage(menuMessage) && (
          <MenuItem onClick={() => runMenuAction(handleEdit)}>
            <ListItemIcon className={classes.menuIcon}>
              <EditIcon />
            </ListItemIcon>
            {i18n.t("chatMessages.edit")}
          </MenuItem>
        )}
        <MenuItem
          className={classes.menuDanger}
          onClick={() => runMenuAction(handleDelete)}
        >
          <ListItemIcon className={classes.menuIcon}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          {i18n.t("chatMessages.delete")}
        </MenuItem>
      </Menu>

      <DocumentModal
        open={documentModalOpen}
        onClose={() => setDocumentModalOpen(false)}
        document={selectedDocument}
      />
    </>
  );
}

const FileInput = (props) => {
  const { handleChangeMedias, disableOption } = props;
  const classes = useStyles();
  return (
    <>
      <input
        multiple
        type="file"
        id="upload-button"
        disabled={disableOption}
        className={classes.uploadInput}
        onChange={handleChangeMedias}
      />
      <label htmlFor="upload-button" style={{ display: "flex" }}>
        <IconButton
          className={classes.barButton}
          aria-label="Anexar arquivo"
          component="span"
          disabled={disableOption}
        >
          <AddIcon />
        </IconButton>
      </label>
    </>
  );
};

const MessageWithLineBreaks = ({
  text,
  replyTo,
  scrollToMessage,
  forwardedFrom,
  checkMessageMedia,
}) => {
  const classes = useStyles();

  if (!text && !replyTo && !forwardedFrom) return null;

  return (
    <div
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        width: "100%",
      }}
    >
      {forwardedFrom && (
        <div>
          <div className={classes.forwardedMessageHeader}>
            <ForwardIcon />
            <span>{i18n.t("chatMessages.forwarded")}</span>
          </div>
          {forwardedFrom.mediaPath && checkMessageMedia(forwardedFrom)}
        </div>
      )}
      {replyTo && (
        <div
          className={classes.replyPreview}
          onClick={() => scrollToMessage(replyTo.id)}
          title={i18n.t("chatMessages.clickToGoToOriginal")}
        >
          <div className={classes.replyPreviewSender}>
            {replyTo.sender?.name || "Mensagem"}
          </div>
          <div className={classes.replyPreviewText}>
            {replyTo.message || "[mensagem não encontrada]"}
          </div>
        </div>
      )}
      <MarkdownWrapper>{text}</MarkdownWrapper>
    </div>
  );
};
